import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store/store';
import type { StoreLocatorService } from '../types/physicalStoreServiceContracts';
import { toStoreCityGroupsVm } from '../viewModels/physicalStore.vm';
import type { StoreCityGroupVm } from '../viewModels/physicalStore.vm';
import {
  selectStoreLocatorInitialData,
  STORE_LOCATOR_INITIAL_SLICE_NAME,
  type StoreLocatorInitialState,
} from '../store/storeLocatorInitialSlice';
import type { StoreLocatorData } from '../../domain/models/StoreLocatorData';

type UseStoreLocatorArgs = {
  service: StoreLocatorService;
};

export function useStoreLocator({ service }: UseStoreLocatorArgs) {
  const rtkData = useSelector((state: RootState) => service.selectStoreLocator(state));
  const ssrData = useSelector((state: { [STORE_LOCATOR_INITIAL_SLICE_NAME]?: StoreLocatorInitialState }) =>
    selectStoreLocatorInitialData(state)
  );

  const storeLocatorData: StoreLocatorData | undefined =
    rtkData?.cityToStoresMap != null ? rtkData : (ssrData ?? undefined);

  useEffect(() => {
    if (storeLocatorData?.cityToStoresMap != null) return;
    service.prefetchStoreLocator();
  }, [service, storeLocatorData?.cityToStoresMap]);

  const cityToStoresMap = storeLocatorData?.cityToStoresMap;
  const isLoading = cityToStoresMap === undefined;

  const cityGroups = useMemo<StoreCityGroupVm[]>(
    () => toStoreCityGroupsVm(service.getSortedCityGroups(cityToStoresMap)),
    [service, cityToStoresMap]
  );

  const storeCount =
    typeof storeLocatorData?.numberOfStores === 'number' ? storeLocatorData.numberOfStores : null;

  return {
    cityToStoresMap,
    cityGroups,
    isLoading,
    storeCount,
    seoSchema: storeLocatorData?.seoSchema,
    currentCityName: storeLocatorData?.currentCityName,
    currentCityIsInList: storeLocatorData?.currentCityIsInList,
  };
}
